import { GraphId } from "components/Graph/types.ts";
import { GeneralGraphUtils, MetricValueTypeObject } from "components/Graph/utils/GeneralGraphUtils.ts";
import { useCurrencies } from "hooks/useCurrencies.ts";
import { CurrencyCode, DEFAULT_CURRENCY, formatCurrency } from "utils/formatCurrency.ts";
import { formatNumber } from "utils/formatNumber.ts";

export const useKeyFormatter = <TData extends object, CPath extends keyof TData>(
    data: TData[],
    key: CPath | undefined,
    graphId: GraphId
) => {
    const { get } = useCurrencies();

    const rawValue = key ? GeneralGraphUtils.getMetricSum(data, key) : 0;
    const rawValueType = GeneralGraphUtils.getMetricType(data, key, graphId);

    return formatKeyValue(rawValue, rawValueType, get);
};

export const formatKeyValue = (
    rawValue: string | number,
    rawValueType: MetricValueTypeObject,
    getter: ReturnType<typeof useCurrencies>["get"]
) => {
    let value: string | number = rawValue;

    const { type } = rawValueType;

    const isMoney = type === "money" && GeneralGraphUtils.isNumber(rawValue);

    if (type === "unknown") {
        value = 0;
    }

    if (type === "string" && GeneralGraphUtils.isString(rawValue)) {
        value = formatNumber(parseFloat(rawValue));
    }

    if (type === "number" && GeneralGraphUtils.isNumber(rawValue)) {
        value = formatNumber(rawValue);
    }

    if (type === "percent" && GeneralGraphUtils.isNumber(rawValue)) {
        value = `${rawValue}%`;
    }

    if (isMoney) {
        if (!getter) {
            value = rawValue;
        } else {
            const { currency_code } = getter(rawValueType.currency_id) || {};
            value = formatCurrency(rawValue, (currency_code as CurrencyCode) || DEFAULT_CURRENCY);
        }
    }

    return value;
};
